"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { VisuallyHidden } from "radix-ui";
import { usePipelineUiStore } from "@/features/jobs/store/pipeline/ui";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";

import { Sheet } from "@/components/ui/sheet";
import { formatShortDate } from "@/lib/date/format-short-date";
import { Icon, type IconName } from "@/components/ui/icon";
import { Button, SubmitButton } from "@/components/ui/button";
import { LinkButton } from "@/components/ui/link-button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/input";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  getPermissionErrorMessage,
  isPermissionDenied,
} from "@/lib/permissions";
import { customInstance } from "@/lib/api/client";
import {
  useCancelImport,
  useDeleteImport,
  useGetProgress,
  useListImports,
  useRetryImport,
  getGetProgressQueryKey,
  getListImportsQueryKey,
} from "@/lib/api/generated";
import type { PipelineStage } from "@/lib/api/types";
import type { ProfileImportItem } from "@/lib/api/model";

import { SyncDirectionIcon } from "@/features/ats-sync/components/sync-direction-icon";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { ProfileImportUploadModal } from "./profile-import-upload-modal";

function AtsImportSheetBanner({
  importing,
  connectorName,
  processingCount,
  isLinked,
  syncDirection,
}: {
  importing: boolean;
  connectorName: string;
  processingCount: number;
  isLinked: boolean;
  syncDirection:
    | "bidirectional"
    | "pull_only"
    | "push_only"
    | "unknown"
    | undefined;
}) {
  if (!importing && processingCount <= 0) return null;
  return (
    <div className="flex items-center gap-2 border-b border-border bg-primary/10 px-4 py-2.5 text-sm text-heading">
      {isLinked ? (
        <SyncDirectionIcon
          direction={syncDirection}
          size={15}
          className="text-primary"
        />
      ) : (
        <Icon
          name="IconArrowDown"
          size={15}
          className="shrink-0 text-primary"
        />
      )}
      <span>
        {importing
          ? `Importing candidates from ${connectorName || "your ATS"}…`
          : `${processingCount} ATS import${processingCount === 1 ? "" : "s"} processing…`}
      </span>
    </div>
  );
}

type TabKey = "successful" | "failed" | "processing";

const TABS: Array<{ key: TabKey; label: string; icon: IconName }> = [
  { key: "successful", label: "Successful", icon: "IconCircleCheck" },
  { key: "failed", label: "Failed", icon: "IconCircleX" },
  { key: "processing", label: "Processing", icon: "IconLoader" },
];

// ProfileImport.status values per tab.
const STATUS_FOR_TAB: Record<TabKey, string[]> = {
  successful: ["successful"],
  failed: ["failed"],
  processing: ["pending", "queued", "processing"],
};

const PER_PAGE = 20;

interface ProfileImportSheetProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  stages: PipelineStage[];
}

export function ProfileImportSheet({
  open,
  onClose,
  jobId,
  stages,
}: ProfileImportSheetProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>("successful");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  const selectedStageId = usePipelineViewStore((s) => s.selectedStageId);

  // Pick up a programmatic tab intent (e.g. clicking the header processing
  // progress bar opens us directly on "Processing"). Consumed once per open
  // so reopening the sheet later falls back to our local default.
  const consumeInitialTab = usePipelineUiStore(
    (s) => s.consumeImportInitialTab,
  );
  useEffect(() => {
    if (!open) return;
    const requested = consumeInitialTab();
    if (requested) {
      setActiveTab(requested);
      setPage(1);
    }
  }, [open, consumeInitialTab]);

  const statusParam = STATUS_FOR_TAB[activeTab];
  const listQuery = useListImports(
    jobId,
    {
      status: statusParam,
      search: search.trim() || undefined,
      page,
      per_page: PER_PAGE,
    },
    {
      query: {
        enabled: open && !!jobId,
        // Refreshes are driven by `useImportProgressSync` (layout-level)
        // which invalidates this query on every count transition. We only
        // force `"always"` on mount so reopening the sheet after something
        // finished doesn't flash stale rows inside the 30s global staleTime.
        refetchOnMount: "always",
      },
    },
  );

  // Shared cache with the layout coordinator + header bar — no extra HTTP.
  const progressQuery = useGetProgress(jobId, {
    query: { enabled: open && !!jobId },
  });

  const retry = useRetryImport();
  const cancel = useCancelImport();
  const remove = useDeleteImport();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getListImportsQueryKey(jobId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetProgressQueryKey(jobId),
    });
    // The pipeline page's query is keyed `["pipeline", jobId, ...]` —
    // invalidating the older `jobPipeline` key was a no-op and left the
    // table stale until a full refresh.
    queryClient.invalidateQueries({
      queryKey: ["pipeline", jobId],
    });
  };

  const handleRetry = (item: ProfileImportItem) => {
    retry.mutate(
      { jobId, importId: item.id },
      {
        onSuccess: () => {
          invalidate();
          setActiveTab("processing");
          setPage(1);
        },
        onError: (err: unknown) => {
          if (isPermissionDenied(err)) {
            showToast.error("Access denied", {
              description: getPermissionErrorMessage(err),
            });
          } else {
            showToast.error("Could not retry import", {
              description: getApiErrorMessage(err),
            });
          }
        },
      },
    );
  };

  const handleCancel = (item: ProfileImportItem) => {
    cancel.mutate(
      { jobId, importId: item.id },
      {
        onSuccess: () => invalidate(),
        onError: (err: unknown) =>
          showToast.error("Could not cancel import", {
            description: getApiErrorMessage(err),
          }),
      },
    );
  };

  const handleDelete = (item: ProfileImportItem) => {
    remove.mutate(
      { jobId, importId: item.id },
      {
        onSuccess: () => invalidate(),
        onError: (err: unknown) =>
          showToast.error("Could not remove import", {
            description: getApiErrorMessage(err),
          }),
      },
    );
  };

  // Bulk delete of every failed import for this job. Uses `customInstance`
  // directly (instead of an Orval hook) so the feature ships without a
  // client regen — the backend endpoint is `DELETE /api/jobs/{id}/imports/failed`.
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const deleteAllFailed = useMutation({
    mutationKey: ["deleteAllFailedImports", jobId],
    mutationFn: async () =>
      customInstance<{ deleted: number }>(`/api/jobs/${jobId}/imports/failed`, {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      invalidate();
      setDeleteAllOpen(false);
      showToast.success(
        `Removed ${data.deleted} failed import${data.deleted === 1 ? "" : "s"}`,
      );
    },
    onError: (err: unknown) => {
      if (isPermissionDenied(err)) {
        showToast.error("Access denied", {
          description: getPermissionErrorMessage(err),
        });
        return;
      }
      showToast.error("Could not clear failed imports", {
        description: getApiErrorMessage(err),
      });
    },
  });

  const items = listQuery.data?.imports ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const progress = progressQuery.data;
  const isImporting = (progress?.processing ?? 0) > 0;
  const {
    importing: atsImporting,
    connectorName: atsName,
    isLinked: atsLinked,
    syncDirection: atsSyncDirection,
  } = useJobAtsLink(jobId);
  const atsProcessing =
    activeTab === "processing"
      ? items.filter((i) => i.source_type === "ats").length
      : (progress?.processing ?? 0);

  // Batch-scoped importing counter: when `progress.processing` transitions
  // from 0 → N we snapshot `total - processing` as the "baseline" of
  // already-settled imports, so the pill says "1 of 3" (this upload) rather
  // than "51 of 53" (this upload + everything that ever completed before).
  // When processing returns to 0, the snapshot clears so the next batch
  // starts fresh.
  const batchBaselineRef = useRef<number | null>(null);
  useEffect(() => {
    if (!progress) return;
    if (progress.processing > 0 && batchBaselineRef.current === null) {
      batchBaselineRef.current = progress.total - progress.processing;
    } else if (progress.processing === 0 && batchBaselineRef.current !== null) {
      batchBaselineRef.current = null;
    }
  }, [progress]);

  const batchStats = useMemo(() => {
    if (!progress || batchBaselineRef.current === null) return null;
    const size = Math.max(0, progress.total - batchBaselineRef.current);
    const done = Math.max(0, size - progress.processing);
    if (size === 0) return null;
    return { size, done };
  }, [progress]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => (!next ? onClose() : undefined)}
        size="md"
      >
        <VisuallyHidden.Root asChild>
          <DialogPrimitive.Title>Profile Imports</DialogPrimitive.Title>
        </VisuallyHidden.Root>
        <div className="flex h-full min-h-0 flex-col bg-surface-primary">
          {/* Header */}
          <div className="flex flex-col gap-3 border-b border-border bg-surface-header p-4">
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <h2 className="text-lg font-medium text-heading leading-normal">
                  Profile Imports
                </h2>
                <p className="text-sm text-contrast/60 leading-normal">
                  Manage import sync, failures, and add profiles.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <LinkButton
                  href="https://docs.neuroscale.ai/core/profile-import"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="sm"
                  className="border-contrast/30 bg-transparent text-contrast/60 hover:bg-contrast/4 hover:text-contrast/60 active:bg-contrast/6"
                  icon={<Icon name="IconNewspaper1" size={15} />}
                >
                  Docs
                </LinkButton>
                <Button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  variant="primary"
                  size="sm"
                  icon={<Icon name="IconPeopleAdd" size={15} />}
                >
                  Upload
                </Button>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  className="flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70"
                >
                  <Icon name="IconCrossMedium" size={16} />
                </button>
              </div>
            </div>

            {/* Tabs — same Vercel-style animated indicator as job detail nav */}
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v as TabKey);
                setPage(1);
              }}
              size="default"
            >
              <TabsList className="h-8 w-full">
                {TABS.map((tab) => {
                  const count =
                    tab.key === "successful"
                      ? (progress?.successful ?? 0)
                      : tab.key === "failed"
                        ? (progress?.failed ?? 0)
                        : (progress?.processing ?? 0);
                  return (
                    <TabsTrigger
                      key={tab.key}
                      value={tab.key}
                      className="min-w-0 flex-1"
                    >
                      <Icon
                        name={tab.icon}
                        className="relative text-foreground"
                      />
                      {tab.label}
                      {progress != null && count > 0 && (
                        <span
                          className={cn(
                            "ml-1 inline-flex min-w-4 shrink-0 items-center justify-center rounded-full bg-contrast/6 px-1.5 text-[11px] font-medium",
                            activeTab === tab.key
                              ? "text-heading"
                              : "text-muted-foreground",
                          )}
                        >
                          {count}
                        </span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>

          {/* Filter row */}
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <SearchInput
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search"
                className="w-[183px]"
              />
            </div>
            <div className="flex items-center gap-3">
              {activeTab === "failed" && (progress?.failed ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setDeleteAllOpen(true)}
                  disabled={deleteAllFailed.isPending}
                  className="flex items-center gap-1 text-sm font-medium text-contrast/50 transition-colors cursor-pointer hover:text-danger disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon name="IconTrashCanSimple" size={14} />
                  Clear all
                </button>
              )}
              {isImporting && batchStats && (
                <ImportingPill done={batchStats.done} size={batchStats.size} />
              )}
            </div>
          </div>

          <AtsImportSheetBanner
            importing={atsImporting}
            connectorName={atsName}
            processingCount={
              atsImporting ? (progress?.processing ?? 0) : atsProcessing
            }
            isLinked={atsLinked}
            syncDirection={atsSyncDirection}
          />

          {/* Rows */}
          <ScrollArea className="flex-1 overflow-y-auto">
            {listQuery.isLoading ? (
              <div className="flex flex-col">
                {Array.from({ length: 5 }).map((_, i) => (
                  <RowSkeleton key={i} />
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                tab={activeTab}
                onUpload={() => setUploadOpen(true)}
              />
            ) : (
              <div className="flex flex-col">
                {items.map((item) => (
                  <ImportRow
                    key={item.id}
                    item={item}
                    onRetry={() => handleRetry(item)}
                    onCancel={() => handleCancel(item)}
                    onDelete={() => handleDelete(item)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Paginator — only when something to paginate */}
          {total > PER_PAGE && (
            <div className="flex items-center justify-between border-t border-border bg-surface-header px-4 py-2.5">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex h-7 cursor-pointer items-center gap-2 rounded-md border border-contrast/5 px-1.5 text-xs text-contrast/70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon name="IconChevronLeft" size={11} />
                Prev
              </button>
              <div className="flex items-center gap-3">
                {Array.from({ length: Math.min(totalPages, 4) }).map((_, i) => {
                  const p = i + 1;
                  const active = p === page;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={
                        "size-7 rounded-md border px-2.5 text-xs font-medium text-heading " +
                        (active
                          ? "border-contrast/15 bg-contrast/4"
                          : "border-contrast/5 cursor-pointer hover:bg-contrast/3")
                      }
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="flex h-7 cursor-pointer items-center gap-2 rounded-md border border-contrast/5 px-1.5 text-xs text-contrast/70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <Icon name="IconChevronRight" size={11} />
              </button>
            </div>
          )}
        </div>
      </Sheet>

      <ProfileImportUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        jobId={jobId}
        stages={stages}
        initialStageId={selectedStageId}
        onUploaded={() => {
          invalidate();
          setActiveTab("processing");
          setPage(1);
        }}
      />

      <DeleteAllFailedConfirm
        open={deleteAllOpen}
        failedCount={progress?.failed ?? 0}
        loading={deleteAllFailed.isPending}
        onClose={() => {
          if (!deleteAllFailed.isPending) setDeleteAllOpen(false);
        }}
        onConfirm={() => deleteAllFailed.mutate()}
      />
    </>
  );
}

// ── Supporting components ────────────────────────────────────────────────────

function DeleteAllFailedConfirm({
  open,
  failedCount,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  failedCount: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => (!next ? onClose() : undefined)}
      dismissible={!loading}
    >
      <ModalContent size="sm">
        <ModalHeader
          title="Delete all failed imports"
          iconName="IconTrashCanSimple"
        />
        <ModalBody>
          <p className="text-sm leading-normal text-contrast/70">
            This will permanently remove{" "}
            <span className="font-medium text-heading">
              {failedCount} failed import{failedCount === 1 ? "" : "s"}
            </span>{" "}
            from this job. The uploaded resume files are deleted with them and
            this can&apos;t be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <SubmitButton
            type="button"
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            loading={loading}
            loadingLabel="Deleting"
          >
            Delete all
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ImportingPill({ done, size }: { done: number; size: number }) {
  // Scoped to the current upload batch — not the lifetime job total. Once
  // a pill appears showing "1 of 3", the denominator is the user's batch
  // size and the numerator ticks up as each file finishes.
  const clampedDone = Math.min(size, Math.max(0, done));
  const percent = size > 0 ? (clampedDone / size) * 100 : 0;
  return (
    <div className="flex h-7 items-center gap-2 rounded-md border border-contrast/5 bg-contrast/3 px-1.5">
      <Icon
        name="IconLoadingCircle"
        size={14}
        className="animate-spin text-heading opacity-70"
      />
      <span className="text-sm font-medium text-heading">Importing</span>
      <div className="relative h-1.5 w-[80px] overflow-hidden rounded-full bg-contrast/10">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-contrast/80 transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-sm text-contrast/85">
        {clampedDone}
        <span className="text-contrast/50"> / {size}</span>
      </span>
    </div>
  );
}

function ImportRow({
  item,
  onRetry,
  onCancel,
  onDelete,
}: {
  item: ProfileImportItem;
  onRetry: () => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const meta = sourceMeta(item.source_type);
  const isFailed = item.status === "failed";
  const isProcessing = ["pending", "queued", "processing"].includes(
    item.status,
  );
  const createdAt = formatShortDate(item.created_at);
  const displayName =
    item.full_name?.trim() ||
    item.source_filename ||
    item.linkedin_url ||
    "Untitled";

  return (
    <div className="flex flex-col border-b border-border bg-contrast/1">
      <div className="flex min-h-10 items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <Icon
            name={meta.rowIcon}
            size={14}
            className="shrink-0 text-heading opacity-70"
          />
          <span className="truncate text-sm font-medium text-heading">
            {displayName}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden items-center gap-1.5 text-sm text-contrast/80 sm:flex">
            <Icon name={meta.sourceIcon} size={14} className="opacity-70" />
            <span>{meta.label}</span>
          </div>
          <Dot />
          <span className="hidden text-sm text-contrast/60 sm:inline">
            {createdAt}
          </span>
          <Dot className="hidden sm:block" />
          <StatusBadge status={item.status} />
          <RowActions
            item={item}
            isFailed={isFailed}
            isProcessing={isProcessing}
            onRetry={onRetry}
            onCancel={onCancel}
            onDelete={onDelete}
          />
        </div>
      </div>
      {isFailed && item.failure_reason && (
        <div className="px-4 pb-3">
          <div className="flex items-start gap-2 rounded-md border border-contrast/5 bg-contrast/[0.02] px-3 py-2">
            <Icon
              name="IconExclamationTriangle"
              size={14}
              className="mt-0.5 shrink-0 text-destructive dark:text-[#ff888a] opacity-90"
            />
            <span className="text-sm text-contrast/60 leading-5">
              {item.failure_reason}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "successful") {
    return (
      <Badge variant="success" size="xs" className="rounded-md">
        Successful
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge
        variant="default"
        size="xs"
        className="rounded-md border-destructive/20 bg-destructive/10 text-destructive dark:border-[rgba(220,102,104,0.1)] dark:bg-[rgba(220,102,104,0.1)] dark:text-[#ff888a]"
      >
        Failed
      </Badge>
    );
  }
  if (status === "cancelled") {
    return (
      <Badge variant="muted" size="xs" className="rounded-md">
        Cancelled
      </Badge>
    );
  }
  return (
    <Badge variant="warning" size="xs" className="rounded-md">
      Processing
    </Badge>
  );
}

function RowActions({
  item,
  isFailed,
  isProcessing,
  onRetry,
  onCancel,
  onDelete,
}: {
  item: ProfileImportItem;
  isFailed: boolean;
  isProcessing: boolean;
  onRetry: () => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  if (isFailed) {
    return (
      <div className="flex items-center gap-1">
        {item.retry_action === "retry" && (
          <button
            type="button"
            onClick={onRetry}
            className="flex h-6 cursor-pointer items-center gap-1 rounded border border-contrast/8 bg-contrast/4 px-2 text-xs font-medium text-heading hover:bg-contrast/8"
          >
            <Icon
              name="IconArrowDownCircle"
              size={12}
              className="rotate-180 opacity-70"
            />
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="flex size-6 cursor-pointer items-center justify-center rounded text-contrast/40 hover:bg-contrast/4 hover:text-contrast/70"
          aria-label="Remove"
        >
          <Icon name="IconTrashCanSimple" size={14} />
        </button>
      </div>
    );
  }
  if (isProcessing) {
    return (
      <button
        type="button"
        onClick={onCancel}
        className="flex size-6 cursor-pointer items-center justify-center rounded text-contrast/40 hover:bg-contrast/4 hover:text-contrast/70"
        aria-label="Cancel"
      >
        <Icon name="IconCrossMedium" size={12} />
      </button>
    );
  }
  return null;
}

function RowSkeleton() {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3 border-b border-border px-4 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <div className="size-3.5 shimmer shimmer-bg text-foreground rounded bg-contrast/6" />
        <div className="h-3 w-24 shimmer shimmer-bg text-foreground rounded bg-contrast/6" />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-3 w-16 shimmer shimmer-bg text-foreground rounded bg-contrast/4" />
        <div className="h-3 w-14 shimmer shimmer-bg text-foreground rounded bg-contrast/4" />
        <div className="h-4 w-16 shimmer shimmer-bg text-foreground rounded-md bg-contrast/5" />
      </div>
    </div>
  );
}

// File-local; distinct copy from the shared @/components/ui/empty-state (ARBI-851).
function EmptyState({ tab, onUpload }: { tab: TabKey; onUpload: () => void }) {
  if (tab === "successful") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-14 text-center">
        <div className="flex size-14 items-center justify-center rounded-xl border border-contrast/6 bg-contrast/4 backdrop-blur-sm">
          <Icon
            name="IconPeopleAdd"
            size={24}
            className="text-heading opacity-30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-[15px] font-medium text-heading">
            No imports yet
          </h3>
          <p className="max-w-[320px] text-[13px] text-contrast/50">
            Upload resumes to add candidates to this job.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={onUpload}>
          Upload resumes
        </Button>
      </div>
    );
  }
  if (tab === "failed") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-14 text-center">
        <div className="flex size-14 items-center justify-center rounded-xl border border-contrast/6 bg-contrast/4 backdrop-blur-sm">
          <Icon
            name="IconCircleCheck"
            size={24}
            className="text-[#15803d] dark:text-[#66dc7e] opacity-70"
          />
        </div>
        <p className="text-[13px] text-contrast/50">
          No failed imports, you&apos;re all clear.
        </p>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-xl border border-contrast/6 bg-contrast/4 backdrop-blur-sm">
        <Icon name="IconLoader" size={24} className="text-heading opacity-30" />
      </div>
      <p className="text-[13px] text-contrast/50">
        No imports are currently processing.
      </p>
    </div>
  );
}

function Dot({ className }: { className?: string }) {
  return (
    <div
      className={
        "size-[3px] shrink-0 rounded-full bg-contrast/30 " + (className ?? "")
      }
    />
  );
}

// ── metadata helpers ────────────────────────────────────────────────────────

const SOURCE_META: Record<
  string,
  { label: string; sourceIcon: IconName; rowIcon: IconName }
> = {
  pdf: {
    label: "PDF Import",
    sourceIcon: "IconFileBend",
    rowIcon: "IconFileBend",
  },
  docx: {
    label: "DOCX Import",
    sourceIcon: "IconFileText",
    rowIcon: "IconFileText",
  },
  rtf: {
    label: "RTF Import",
    sourceIcon: "IconFileText",
    rowIcon: "IconFileText",
  },
  txt: {
    label: "Text Import",
    sourceIcon: "IconFileText",
    rowIcon: "IconFileText",
  },
  image: {
    label: "Image Import",
    sourceIcon: "IconFileChart",
    rowIcon: "IconFileChart",
  },
  csv: {
    label: "CSV Import",
    sourceIcon: "IconFileChart",
    rowIcon: "IconFileChart",
  },
  url: {
    label: "Search Import",
    sourceIcon: "IconWorld",
    rowIcon: "IconWorld",
  },
  ats: { label: "ATS Import", sourceIcon: "IconPuzzle", rowIcon: "IconPuzzle" },
};

function sourceMeta(source: string) {
  return (
    SOURCE_META[source] ?? {
      label: "Import",
      sourceIcon: "IconFileBend" as const,
      rowIcon: "IconFileBend" as const,
    }
  );
}
